import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { InventoryLevel } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useSetReorderPoint } from '../hooks/use-inventory-mutations';
import {
  reorderPointSchema,
  type ReorderPointFormInput,
  type ReorderPointFormOutput,
} from '../schemas/reorder-point-schema';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['reorderPoint']);

export interface ReorderPointDialogProps {
  storeId: string;
  level: InventoryLevel;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The low-stock threshold of one variant at one location; blank clears it. */
export function ReorderPointDialog({
  storeId,
  level,
  open,
  onOpenChange,
}: ReorderPointDialogProps) {
  const { t } = useTranslation(['inventory', 'common']);
  const message = useErrorMessage();
  const save = useSetReorderPoint(storeId);
  const form = useForm<ReorderPointFormInput, unknown, ReorderPointFormOutput>({
    resolver: useI18nResolver(reorderPointSchema),
    defaultValues: { reorderPoint: level.reorderPoint === null ? '' : String(level.reorderPoint) },
  });

  const close = () => {
    onOpenChange(false);
    save.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      await save.mutateAsync({
        variantId: level.variantId,
        locationId: level.locationId,
        reorderPoint: values.reorderPoint,
      });
      close();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('inventory:reorderPoint.title')}</DialogTitle>
          <DialogDescription>
            {t('inventory:reorderPoint.description', {
              product: level.productTitle,
              variant: level.variantTitle,
            })}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="reorderPoint"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('inventory:reorderPoint.field')}</FormLabel>
                  <FormControl>
                    <Input
                      inputMode="numeric"
                      autoComplete="off"
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormDescription>{t('inventory:reorderPoint.hint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? t('common:actions.saving') : t('common:actions.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
